import './styles.scss';

import { faArrowRotateRight, faPlus, faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Tooltip } from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';

import { BLUR_STYLE, useDemoBlur } from '../../../providers/demo-blur';
import { VenueContext } from '../../../providers/venue-provider';

import NewAccountModal from './connect-a-new-account-modal';
import DeleteAccountModal from './delete-account-modal';

import TableGrid from '../../../components/table-grid';

const AccountManagement = () => {
  const [openNewAccountModal, setOpenNewAccountModal] = useState(false);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteAccount, setDeleteAccount] = useState<string | undefined>();

  const { credentials: accounts, refreshAccounts } = useContext(VenueContext);
  const { isNamesBlurred, setIsNamesBlurred } = useDemoBlur();

  const handleNewApikey = () => {
    setOpenNewAccountModal(true);
  };

  const handleGetAccounts = useCallback(() => {
    refreshAccounts();
  }, [refreshAccounts]);

  const handleClose = useCallback(() => {
    setOpenNewAccountModal(false);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setOpenDeleteModal(false);
    setDeleteAccount(undefined);
  }, []);

  const handleOpenDelete = useCallback((name: string) => {
    setOpenDeleteModal(true);
    setDeleteAccount(name);
  }, []);

  const COLUMNS = [
    {
      field: 'exchange',
      headerName: 'Exchange',
      sortable: false,
      minWidth: 100,
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Name',
      sortable: false,
      minWidth: 100,
      flex: 1,
      cellRenderer: (params: any) => (
        <span style={isNamesBlurred ? BLUR_STYLE : undefined}>{params.value}</span>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      minWidth: 120,
      flex: 1,
      cellRenderer: (params: any) => {
        const { name, exchange } = params.data;
        const disabled = exchange === 'otc';
        return (
          <div className="actions-container">
            <Button
              onClick={() => {
                handleOpenDelete(name);
              }}
              className="delete-action-button"
              disabled={disabled}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    handleGetAccounts();
  }, []);

  return (
    <div className="account-management-container">
      <h1 className="title">Account Management</h1>

      <div className="buttons">
        <Button className="button" onClick={handleGetAccounts}>
          <FontAwesomeIcon icon={faArrowRotateRight} />
          <span>Refresh connections</span>
        </Button>
        <Button className="button button-filled" onClick={handleNewApikey}>
          <FontAwesomeIcon icon={faPlus} />
          <span>Add new API key</span>
        </Button>
        <Tooltip title={isNamesBlurred ? 'Show account names' : 'Blur account names for demo'}>
          <Button
            className="account-management-blur-toggle"
            aria-label={isNamesBlurred ? 'Show account names' : 'Blur account names for demo'}
            onClick={() => setIsNamesBlurred((prev) => !prev)}
          >
            <FontAwesomeIcon icon={isNamesBlurred ? faEyeSlash : faEye} />
          </Button>
        </Tooltip>
      </div>

      <div className="accounts">
        {accounts.map((accountGroup) => {
          if (!accountGroup) return null;

          return (
            <TableGrid
              columns={COLUMNS}
              rowData={accountGroup.items}
              key={accountGroup.exchange}
              className="account-table-grid"
              noWatermark
              autoHeight
            />
          );
        })}
      </div>

      <NewAccountModal open={!!openNewAccountModal} handleClose={handleClose} />
      <DeleteAccountModal
        open={!!openDeleteModal}
        handleClose={handleCloseDelete}
        account={deleteAccount}
      />
    </div>
  );
};

export default AccountManagement;
